// @ts-nocheck
"use client"

import { useState, useEffect, useRef } from "react"
import ReactMarkdown from "react-markdown"
import { DraftMarkdownSnippet } from "./DraftMarkdownSnippet"
import UnifiedSectionEditor from "./UnifiedSectionEditor"
import useApiCalls from "../hooks/useApiCalls"
import { renderTemplate, TEMPLATE_NAMES } from "../templates/TemplateRenderer.js"
import * as Tooltip from "@radix-ui/react-tooltip"

const CaseStudyViewer = ({
  caseStudy,
  onDownload,
  onFeedback,
  onStartOver,
  versionInfo = null,
  hasVersions = false,
  originalCaseStudy = null,
  revisionJobId = null,
  baselineJobId = null, // For edit operations after merges
  onMergeSections = null,
}) => {
  // View mode state - now includes merge for post-revision workflows
  const [viewMode, setViewMode] = useState("unified") // 'unified' or 'merge'

  // Section editing state for unified view
  const [expandedSections, setExpandedSections] = useState({})
  const [sectionEditModes, setSectionEditModes] = useState({}) // 'custom' or 'feedback' or null
  const [customContent, setCustomContent] = useState({})
  const [sectionFeedback, setSectionFeedback] = useState({})

  // Multi-section feedback state
  const [showMultiFeedback, setShowMultiFeedback] = useState(false)
  const [multiFeedbackText, setMultiFeedbackText] = useState("")
  const [selectedSectionsForFeedback, setSelectedSectionsForFeedback] = useState({})

  // Merge functionality state
  const [sectionSelections, setSectionSelections] = useState({})
  const [mergeCustomContent, setMergeCustomContent] = useState({})
  const [showRestrictedModal, setShowRestrictedModal] = useState(false)
  const [restrictedModalType, setRestrictedModalType] = useState(null) // 'why' | 'cta'
  const [dismissedRestrictedModals, setDismissedRestrictedModals] = useState(() => {
    try {
      const saved = sessionStorage.getItem('dismissedRestrictedModals')
      return saved ? JSON.parse(saved) : { why: false, cta: false }
    } catch (_) {
      return { why: false, cta: false }
    }
  })
  // Sections that are immutable/prewritten and cannot receive AI revisions
  const restrictedSections = new Set(["why_choose_riscpoint", "call_to_action"])


  // UI state
  const [submitting, setSubmitting] = useState(false)
  const [validationError, setValidationError] = useState("")
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false)
  const [showStartOverConfirm, setShowStartOverConfirm] = useState(false)
  const [showUnsavedChangesConfirm, setShowUnsavedChangesConfirm] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState(null)
  const [isApplyBannerSticky, setIsApplyBannerSticky] = useState(false)
  const [isMergeBannerSticky, setIsMergeBannerSticky] = useState(false)
  
  // HTML Preview state
  const [showHtmlPreview, setShowHtmlPreview] = useState(false)
  const [htmlPreviewLoading, setHtmlPreviewLoading] = useState(false)
  const [htmlPreviewError, setHtmlPreviewError] = useState("")
  const [renderedHtml, setRenderedHtml] = useState("")
  const [selectedTemplate, setSelectedTemplate] = useState(TEMPLATE_NAMES.PAGE_1)
  
  // PDF Export state
  const [pdfGenerating, setPdfGenerating] = useState(false)
  
  // Text sizing state - now using REM values directly
  const [textSizingConfig, setTextSizingConfig] = useState({
    // Page 1 sections
    title: '2.2rem',
    subtitle: '1.2rem',
    at_glance: '0.75rem',
    key_metrics_title: '0.9rem',
    key_metrics_description: '0.75rem',
    challenges: '0.75rem',
    objectives: '0.75rem',
    outcomes: '0.9rem',
    testimonial: '0.75rem',
    // Page 2 sections
    partnership_client_overview: '0.8rem',
    partnership_bullets: '0.7rem',
    why_choose_riscpoint: '0.75rem',
    call_to_action: '0.8rem'
  })

  // Slider values state (extracted from rem values)
  const [sliderValues, setSliderValues] = useState({
    // Page 1 sections
    title: 2.2,
    subtitle: 1.2,
    at_glance: 0.75,
    key_metrics_title: 0.9,
    key_metrics_description: 0.75,
    challenges: 0.75,
    objectives: 0.75,
    outcomes: 0.9,
    testimonial: 0.75,
    // Page 2 sections
    partnership_client_overview: 0.8,
    partnership_bullets: 0.7,
    why_choose_riscpoint: 0.75,
    call_to_action: 0.8
  })

  // Current computed sizes from backend/defaults
  const [currentSizes, setCurrentSizes] = useState({
    // Page 1 sections
    title: '2.2rem',
    subtitle: '1.2rem',
    at_glance: '0.75rem',
    key_metrics_title: '1.2rem',
    key_metrics_description: '0.75rem',
    challenges: '0.75rem',
    objectives: '0.6rem',
    outcomes: '0.9rem',
    testimonial: '0.75rem',
    // Page 2 sections
    partnership_client_overview: '0.8rem',
    partnership_bullets: '0.7rem',
    why_choose_riscpoint: '0.75rem',
    call_to_action: '0.8rem'
  })


  // Refs for sticky banner functionality
  const caseStudyHeaderRef = useRef(null)
  const applyBannerRef = useRef(null)
  const mergeButtonRef = useRef(null)
  const previewIframeRef = useRef(null)

  // Post-merge state management
  const [mergeCompleted, setMergeCompleted] = useState(false)
  
  // API calls hook (no longer using renderHtmlTemplate from backend)
  const { submitRevision, mergeSections } = useApiCalls()

  // HTML Preview handler (now using frontend renderer)
  const handleHtmlPreview = () => {
    if (!caseStudy?.data) {
      alert('No case study data available for preview')
      return
    }

    // Store case study data in localStorage for the preview page
    localStorage.setItem('previewCaseStudy', JSON.stringify(caseStudy))
    
    // Navigate to preview page in new tab
    window.open('/preview', '_blank')
  }
  
  // Handler for updating text sizing via slider
  const handleSliderChange = async (section, value) => {
    const numValue = parseFloat(value)
    setSliderValues(prev => ({ ...prev, [section]: numValue }))
    
    const newConfig = { ...textSizingConfig, [section]: `${value}rem` }
    setTextSizingConfig(newConfig)
    
    if (showHtmlPreview) {
      setHtmlPreviewLoading(true)
      try {
        // Use frontend template renderer instead of backend API
        const result = renderTemplate(caseStudy.data, selectedTemplate, newConfig)
        setRenderedHtml(result.html)
        
        // Update current sizes
        setCurrentSizes(prev => ({ ...prev, [section]: `${value}rem` }))
      } catch (error) {
        console.error("Slider update failed:", error)
        setHtmlPreviewError("Failed to update preview. Please try again.")
      } finally {
        setHtmlPreviewLoading(false)
      }
    }
  }


  // Extract computed sizes from backend response
  const updateCurrentSizesFromResult = (result, config) => {
    const newCurrentSizes = { ...currentSizes }
    
    // Map backend computed values to current sizes
    if (result.template_data?.text_sizing_config) {
      const computed = result.template_data.text_sizing_config
      
      // Update with computed auto-fit values or fallback to defaults
      if (computed.challenges_computed) newCurrentSizes.challenges = computed.challenges_computed
      if (computed.solutions_computed) newCurrentSizes.solutions = computed.solutions_computed
      if (computed.benefits_desc_computed) newCurrentSizes.benefits = computed.benefits_desc_computed
      if (computed.title_computed) newCurrentSizes.title = computed.title_computed
      if (computed.subtitle_computed) newCurrentSizes.subtitle = computed.subtitle_computed
      if (computed.at_glance_computed) newCurrentSizes.at_glance = computed.at_glance_computed
      if (computed.key_metrics_title_computed) newCurrentSizes.key_metrics_title = computed.key_metrics_title_computed
      if (computed.key_metrics_bullets_computed) newCurrentSizes.key_metrics_bullets = computed.key_metrics_bullets_computed
    }
    
    // For non-auto-fit settings, use preset values
    Object.keys(config).forEach(section => {
      if (config[section] === 'small') {
        newCurrentSizes[section] = getSizeForPreset(section, 'small')
      } else if (config[section] === 'medium') {
        newCurrentSizes[section] = getSizeForPreset(section, 'medium')
      } else if (config[section] === 'large') {
        newCurrentSizes[section] = getSizeForPreset(section, 'large')
      }
    })
    
    setCurrentSizes(newCurrentSizes)
  }

  // Get preset size values
  const getSizeForPreset = (section, preset) => {
    const presetSizes = {
      challenges: { small: '0.65rem', medium: '0.75rem', large: '0.85rem' },
      solutions: { small: '0.65rem', medium: '0.75rem', large: '0.85rem' },
      benefits: { small: '0.6rem', medium: '0.7rem', large: '0.8rem' },
      title: { small: '1.8rem', medium: '2.2rem', large: '2.6rem' },
      subtitle: { small: '1.0rem', medium: '1.2rem', large: '1.4rem' },
      at_glance: { small: '0.6rem', medium: '0.75rem', large: '0.9rem' },
      key_metrics_title: { small: '0.7rem', medium: '0.9rem', large: '1.0rem' },
      key_metrics_bullets: { small: '0.6rem', medium: '0.75rem', large: '0.85rem' }
    }
    
    return presetSizes[section]?.[preset] || '0.75rem'
  }


  // Handle template switching with automatic re-rendering (now using frontend renderer)
  const handleTemplateSwitch = async (templateName) => {
    setSelectedTemplate(templateName)
    
    // Re-render if preview is open
    if (showHtmlPreview) {
      setHtmlPreviewLoading(true)
      try {
        // Use frontend template renderer instead of backend API
        const result = renderTemplate(caseStudy.data, templateName, textSizingConfig)
        setRenderedHtml(result.html)
        
        // Update current sizes from frontend response
        updateCurrentSizesFromResult(result, textSizingConfig)
      } catch (error) {
        console.error("Template switch failed:", error)
        setHtmlPreviewError("Failed to switch template. Please try again.")
      } finally {
        setHtmlPreviewLoading(false)
      }
    }
  }

  // PDF Export functionality using browser's native print
  const generatePDF = async () => {
    if (!caseStudy?.data || !renderTemplate) {
      alert('Please ensure the case study is loaded before generating PDF')
      return
    }

    setPdfGenerating(true)
    
    try {
      // Get case study title for the print window
      const title = caseStudy?.data?.title || 'Case Study'
      
      // Generate both page templates
      const page1Result = renderTemplate(caseStudy.data, TEMPLATE_NAMES.PAGE_1, textSizingConfig)
      const page2Result = renderTemplate(caseStudy.data, TEMPLATE_NAMES.PAGE_2, textSizingConfig)
      
      // Create combined HTML with proper page breaks
      const combinedHTML = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title.replace(/[<>"']/g, '')}</title>
  <style>
    @page {
      size: 8.5in 11in;
      margin: 0;
    }
    
    @media print {
      body {
        margin: 0;
        padding: 0;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      
      .page {
        width: 8.5in;
        height: 11in;
        page-break-after: always;
        page-break-inside: avoid;
        overflow: hidden;
      }
      
      .page:last-child {
        page-break-after: auto;
      }
      
      /* Ensure images are preserved */
      img {
        max-width: 100% !important;
        height: auto !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      
    }
    
    @media screen {
      body {
        background: #f0f0f0;
        margin: 0;
        padding: 20px;
      }
      
      .page {
        width: 8.5in;
        height: 11in;
        margin: 0 auto 20px auto;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        background: white;
        overflow: hidden;
      }
    }
  </style>
</head>
<body>
  <div class="page">
    ${page1Result.html.replace(/<\/?html[^>]*>/g, '').replace(/<\/?head[^>]*>/g, '').replace(/<\/?body[^>]*>/g, '').replace(/<\/?(!DOCTYPE)[^>]*>/g, '')}
  </div>
  <div class="page">
    ${page2Result.html.replace(/<\/?html[^>]*>/g, '').replace(/<\/?head[^>]*>/g, '').replace(/<\/?body[^>]*>/g, '').replace(/<\/?(!DOCTYPE)[^>]*>/g, '')}
  </div>
</body>
</html>`
      
      // Open new window for printing
      const printWindow = window.open('', '_blank')
      
      if (!printWindow) {
        alert('Please allow popups to generate PDF. Check your browser settings.')
        return
      }
      
      // Write the combined HTML to the new window
      printWindow.document.write(combinedHTML)
      printWindow.document.close()
      
      // Wait for images to load in the print window
      const waitForPrintWindowImages = () => {
        return new Promise((resolve) => {
          const images = printWindow.document.querySelectorAll('img')
          let loadedCount = 0
          const totalImages = images.length
          
          if (totalImages === 0) {
            resolve()
            return
          }
          
          const checkComplete = () => {
            loadedCount++
            if (loadedCount >= totalImages) {
              // Add delay to ensure rendering is complete
              setTimeout(resolve, 1000)
            }
          }
          
          images.forEach(img => {
            if (img.complete) {
              checkComplete()
            } else {
              img.onload = checkComplete
              img.onerror = checkComplete
            }
          })
        })
      }
      
      // Wait for everything to load, then trigger print
      await waitForPrintWindowImages()
      
      // Focus the window and trigger print dialog
      printWindow.focus()
      printWindow.print()
      
      // Close the window after a delay (user might need time to save)
      setTimeout(() => {
        try {
          printWindow.close()
        } catch (e) {
          // Window might already be closed by user
        }
      }, 5000)
      
    } catch (error) {
      console.error('PDF generation failed:', error)
      alert('Failed to generate PDF. Please try again.')
    } finally {
      setPdfGenerating(false)
    }
  }

  // Render slider-only sizing control
  const renderSizingControl = (section, label) => (
    <div key={section} className="flex items-center gap-2 flex-wrap">
      <label className="text-xs text-gray-600 whitespace-nowrap min-w-[80px]">{label}:</label>
      <div className="flex items-center gap-2 flex-1">
        <input
          type="range"
          min="0.5"
          max="3.0"
          step="0.01"
          value={sliderValues[section] || 1.0}
          onChange={(e) => handleSliderChange(section, e.target.value)}
          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
        />
        <span className="text-xs text-gray-500 min-w-[45px] text-right">
          {(sliderValues[section] || 1.0).toFixed(2)}rem
        </span>
      </div>
    </div>
  )

  // Navigation with unsaved changes confirmation
  const handleNavigationWithConfirmation = (targetMode) => {
    const hasUnsavedChanges =
      Object.keys(customContent).some((key) => customContent[key] && customContent[key].toString().trim()) ||
      Object.keys(sectionFeedback).length > 0 ||
      Object.keys(selectedSectionsForFeedback).length > 0 ||
      multiFeedbackText.trim() ||
      Object.keys(mergeCustomContent).some(
        (key) => mergeCustomContent[key] && mergeCustomContent[key].toString().trim(),
      )

    if (hasUnsavedChanges && viewMode === "unified") {
      setPendingNavigation(targetMode)
      setShowUnsavedChangesConfirm(true)
    } else {
      setViewMode(targetMode)
    }
  }

  const confirmNavigation = () => {
    // Clear unsaved changes and navigate
    setCustomContent({})
    setSectionFeedback({})
    setSectionEditModes({})
    setExpandedSections({})
    setMergeCustomContent({})
    setSectionSelections({})
    // Clear multi-feedback state
    setSelectedSectionsForFeedback({})
    setMultiFeedbackText("")
    setShowMultiFeedback(false)
    setViewMode(pendingNavigation)
    setShowUnsavedChangesConfirm(false)
    setPendingNavigation(null)
  }

  const cancelNavigation = () => {
    setShowUnsavedChangesConfirm(false)
    setPendingNavigation(null)
  }

  // Sticky banner functionality
  useEffect(() => {
    const handleScroll = () => {
      if (caseStudyHeaderRef.current && applyBannerRef.current) {
        const headerRect = caseStudyHeaderRef.current.getBoundingClientRect()
        const hasChanges =
          Object.keys(customContent).some((key) => customContent[key] && customContent[key].toString().trim()) ||
          Object.keys(sectionFeedback).length > 0 ||
          Object.keys(selectedSectionsForFeedback).length > 0 ||
          multiFeedbackText.trim()

        // Make apply banner sticky when header goes out of view and there are changes
        setIsApplyBannerSticky(headerRect.bottom < 0 && hasChanges)
      }

      // Handle merge button sticky behavior
      if (mergeButtonRef.current && viewMode === "merge") {
        const mergeButtonRect = mergeButtonRef.current.getBoundingClientRect()
        // Make merge button sticky when it goes out of view
        setIsMergeBannerSticky(mergeButtonRect.bottom < 0)
      }
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [customContent, sectionFeedback, selectedSectionsForFeedback, multiFeedbackText, viewMode])

  // Scroll to top when case study content changes (after merge or revision)
  useEffect(() => {
    if (caseStudy) {
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }, [caseStudy?.metadata?.job_id])

  // Reset merge completed state when there are changes or when switching views
  useEffect(() => {
    if (viewMode === "merge") {
      // Reset merge state when switching to merge tab
      setMergeCompleted(false)
    }
  }, [viewMode])
  
  // Reset merge completed state when user makes manual edits or AI revisions
  useEffect(() => {
    const hasManualChanges = Object.keys(customContent).length > 0 || 
                            Object.keys(sectionFeedback).length > 0
    if (hasManualChanges && mergeCompleted) {
      setMergeCompleted(false)
    }
  }, [customContent, sectionFeedback, mergeCompleted])

  // All possible case study sections with labels
  const getAllPossibleSections = () => [
    { key: "title", label: "Title" },
    { key: "subtitle", label: "Subtitle" },
    { key: "at_a_glance", label: "At a Glance" },
    { key: "key_metrics", label: "Key Metrics" },
    { key: "testimonial", label: "Client Testimonial" },
    { key: "challenges", label: "Challenges" },
    { key: "objectives", label: "Objectives" },
    { key: "outcomes", label: "Outcomes" },
    { key: "partnership_details", label: "Partnership Details" },
    { key: "why_choose_riscpoint", label: "Why Choose RISCPoint" },
    { key: "call_to_action", label: "Call to Action" },
  ]

  // Helper function to check if content actually exists (like in merge view)
  const hasActualContent = (content) => {
    if (!content) return false
    
    if (typeof content === "object" && content !== null) {
      if (Array.isArray(content)) {
        return content.length > 0 && content.some(item => item && String(item).trim())
      } else {
        // For objects, check if any values have actual content
        return Object.values(content).some(value => {
          if (Array.isArray(value)) {
            return value.length > 0 && value.some(item => item && String(item).trim())
          }
          return value && String(value).trim()
        })
      }
    }
    
    return String(content).trim() !== ""
  }

  // Dynamic section detection based on actual case study data
  const getDynamicSections = (caseStudyData) => {
    const allSections = getAllPossibleSections()

    return allSections.map((section) => {
      const content = caseStudyData?.[section.key] || null
      
      // Special handling for nested structures to determine if they exist
      let exists = caseStudyData && hasActualContent(content)
      
      // Additional checks for specific nested structures
      if (section.key === "key_metrics" && Array.isArray(content)) {
        exists = content.length > 0 && content.some(metric => 
          hasActualContent(metric.title) || hasActualContent(metric.description)
        )
      } else if (section.key === "testimonial" && content) {
        exists = hasActualContent(content.quote) &&
                hasActualContent(content.author_name)
      } else if (section.key === "outcomes" && Array.isArray(content)) {
        exists = content.length > 0 && content.some(outcome => 
          hasActualContent(outcome.title) || hasActualContent(outcome.description)
        )
      } else if (section.key === "partnership_details" && content) {
        const details = content.partnership_details || []
        exists = hasActualContent(content.client_overview) || 
                (details.length > 0 && details.some(detail => 
                  hasActualContent(detail.title) || hasActualContent(detail.description)
                ))
      } else if (section.key === "call_to_action" && content) {
        exists = hasActualContent(content.title) || hasActualContent(content.description)
      }
      
      return {
        ...section,
        exists: exists,
        content: content,
      }
    })
  }

  // Get sections for current case study
  const sections = getDynamicSections(caseStudy?.data)

  // Helper function to render section content (handles objects and strings)
  // Helper function to render outcomes with styled subheaders
  const renderOutcomes = (outcomes) => {
    if (!outcomes || !Array.isArray(outcomes)) return null

    return outcomes.map((outcome, index) => {
      return (
        <div key={index} className="mb-4">
          <div className="font-semibold text-gray-800 mb-2">
            <DraftMarkdownSnippet>{outcome.title || ""}</DraftMarkdownSnippet>
          </div>
          <DraftMarkdownSnippet className="max-w-none">{outcome.description || ""}</DraftMarkdownSnippet>
        </div>
      )
    })
  }

  // Helper function to render key metrics
  const renderKeyMetrics = (metrics) => {
    if (!metrics || !Array.isArray(metrics)) return null

    return metrics.map((metric, index) => {
      return (
        <div key={index} className="mb-4">
          <div className="font-semibold text-gray-800 mb-2">
            <DraftMarkdownSnippet>{metric.title || ""}</DraftMarkdownSnippet>
          </div>
          <DraftMarkdownSnippet className="max-w-none">{metric.description || ""}</DraftMarkdownSnippet>
        </div>
      )
    })
  }

  // Helper function to render testimonial
  const renderTestimonial = (testimonial) => {
    if (!testimonial) return null

    return (
      <div className="mb-4">
        <blockquote className="italic text-gray-600 border-l-4 border-gray-300 pl-4 mb-2">
          <DraftMarkdownSnippet className="max-w-none">{testimonial.quote || ""}</DraftMarkdownSnippet>
        </blockquote>
        <div className="text-right text-gray-700">
          <DraftMarkdownSnippet>
            {`— ${testimonial.author_name || ""}${testimonial.author_title ? `, ${testimonial.author_title}` : ""}${testimonial.company ? `, ${testimonial.company}` : ""}`}
          </DraftMarkdownSnippet>
        </div>
      </div>
    )
  }

  // Helper function to render partnership details
  const renderPartnershipDetails = (partnershipDetails) => {
    if (!partnershipDetails) return null

    return (
      <div>
        <div className="mb-4">
          <h4 className="font-semibold text-gray-800 mb-2">Client Overview</h4>
          <DraftMarkdownSnippet className="max-w-none">{partnershipDetails.client_overview || ""}</DraftMarkdownSnippet>
        </div>
        {partnershipDetails.partnership_details.map((detail, index) => (
          <div key={index} className="mb-4">
            <div className="font-semibold text-gray-800 mb-2">
              <DraftMarkdownSnippet>{detail.title || ""}</DraftMarkdownSnippet>
            </div>
            <DraftMarkdownSnippet className="max-w-none">{detail.description || ""}</DraftMarkdownSnippet>
          </div>
        ))}
      </div>
    )
  }

  // Helper function to render call to action
  const renderCallToAction = (callToAction) => {
    if (!callToAction) return null

    return (
      <div className="mb-4">
        <div className="font-semibold text-gray-800 mb-2">
          <DraftMarkdownSnippet>{callToAction.title || ""}</DraftMarkdownSnippet>
        </div>
        <DraftMarkdownSnippet className="max-w-none">{callToAction.description || ""}</DraftMarkdownSnippet>
      </div>
    )
  }

  const renderSectionContent = (content, sectionKey) => {
    if (!content) return ""

    // Handle specific nested structures based on section key
    if (sectionKey === "key_metrics" && Array.isArray(content)) {
      return content.map(metric => `- **${metric.title}**: ${metric.description}`).join("\n")
    }

    if (sectionKey === "testimonial" && typeof content === "object") {
      return `"${content.quote}"\n\n— ${content.author_name}, ${content.author_title}, ${content.company}`
    }

    if (sectionKey === "outcomes" && Array.isArray(content)) {
      return content.map(outcome => `• ${outcome.title}:\n  ${outcome.description}`).join("\n\n")
    }

    if (sectionKey === "partnership_details" && typeof content === "object") {
      const details = content.partnership_details || []
      return `Client Overview:\n${content.client_overview}\n\n` + 
        details.map(detail => `• ${detail.title}:\n  ${detail.description}`).join("\n\n")
    }

    if (sectionKey === "call_to_action" && typeof content === "object") {
      return `${content.title}\n\n${content.description}`
    }

    // Handle basic types
    if (typeof content === "string") {
      const cleanedContent = content.replace(/^"(.*)"$/, "$1")
      // Debug log for title content issues
      if (import.meta.env.DEV && content.includes("Updated per feedback")) {
        console.log("🔍 Debug: Found 'Updated per feedback' in content:", content)
        console.log("🔍 Debug: Cleaned content:", cleanedContent)
      }
      return cleanedContent
    }

    if (typeof content === "object" && content !== null) {
      if (Array.isArray(content)) {
        // Markdown list items: a single "\n" between plain lines collapses into one paragraph
        return content.map((item) => {
          if (typeof item === "string") {
            return `- ${item}`
          } else if (typeof item === "object") {
            // Handle array of objects (like outcomes or metrics)
            const title = item.title || ""
            const description = item.description || ""
            return `- ${title}: ${description}`
          }
          return `- ${String(item)}`
        }).join("\n")
      } else {
        return Object.entries(content)
          .map(([key, value]) => {
            if (Array.isArray(value)) {
              if (key.toLowerCase() === "bullet_points") {
                return value.map((item) => `• ${item}`).join("\n")
              }
              return `${key}:\n${value.map((item) => `• ${item}`).join("\n")}`
            }
            const cleanedValue = typeof value === "string" ? value.replace(/^"(.*)"$/, "$1") : value
            // Don't add key prefix for explanation field - just return the content directly
            if (key.toLowerCase() === "explanation") {
              return cleanedValue
            }
            // Format outcomes subheaders with proper styling
            if (typeof cleanedValue === "string" && cleanedValue.length > 0) {
              return `${key}\n${cleanedValue}`
            }
            return `${key}: ${cleanedValue}`
          })
          .join("\n\n")
      }
    }

    return String(content)
  }

  // Helper function to get dynamic section name from content
  const getDynamicSectionName = (section, content) => {
    if (!content) return section.label

    // For title and subtitle, use the actual content as the name (cleaned up)
    if (section.key === "title" || section.key === "subtitle") {
      let cleanContent = content

      // Handle different content types
      if (typeof content === "string") {
        cleanContent = content.replace(/^"(.*)"$/, "$1").trim()
      } else if (typeof content === "object" && content !== null) {
        // If it's an object, try to extract meaningful text
        cleanContent = String(content)
          .replace(/^"(.*)"$/, "$1")
          .trim()
      }

      return truncateText(cleanContent, 60) // Increased length for better readability
    }

    // For other sections, use the static label
    return section.label
  }

  // Helper function to truncate text
  const truncateText = (text, maxLength) => {
    if (!text) return ""
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength).trim() + "..."
  }

  // Toggle section expansion
  const toggleSection = (sectionKey) => {
    const isExpanding = !expandedSections[sectionKey]
    
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }))
    
    // When expanding a missing section, automatically set the appropriate edit mode
    if (isExpanding) {
      const section = sections.find(s => s.key === sectionKey)
      if (section && !section.exists) {
        if (sectionKey === "testimonial") {
          // Client testimonial uses custom edit mode
          setSectionEditMode(sectionKey, "custom")
        } else {
          // Other missing sections use feedback mode
          setSectionEditMode(sectionKey, "feedback")
        }
      }
    }
  }

  // Set edit mode for a section
  const setSectionEditMode = (sectionKey, mode) => {
    // Block AI revision mode for restricted sections
    if (restrictedSections.has(sectionKey) && mode === "feedback") {
      setValidationError("AI revisions are disabled for this prewritten section.")
      return
    }

    // Show warning before allowing custom edits on restricted sections
    if (restrictedSections.has(sectionKey) && mode === "custom") {
      const type = sectionKey === 'why_choose_riscpoint' ? 'why' : 'cta'
      // Only show once per session per section type
      if (!dismissedRestrictedModals[type]) {
        setRestrictedModalType(type)
        setShowRestrictedModal(true)
      }
    }

    // Only prevent custom edits when section is selected for multi-feedback
    if (selectedSectionsForFeedback[sectionKey] && mode === "custom") {
      setValidationError(
        "Cannot use custom edits for sections selected for AI mass edit. AI feedback can still be added individually.",
      )
      return
    }

    setSectionEditModes((prev) => ({
      ...prev,
      [sectionKey]: prev[sectionKey] === mode ? null : mode,
    }))

    // Clear the other mode's data when switching
    if (mode === "custom") {
      // Don't clear individual AI feedback when switching to custom mode
      // Only clear if not using multi-feedback for this section
      if (!selectedSectionsForFeedback[sectionKey]) {
        setSectionFeedback((prev) => {
          const newFeedback = { ...prev }
          delete newFeedback[sectionKey]
          return newFeedback
        })
      }
    } else if (mode === "feedback") {
      setCustomContent((prev) => {
        const newContent = { ...prev }
        delete newContent[sectionKey]
        return newContent
      })
    }

    // Clear validation error when successfully switching modes
    setValidationError("")
  }

  // Handle custom content changes
  const handleCustomContentChange = (sectionKey, content) => {
    setCustomContent((prev) => ({
      ...prev,
      [sectionKey]: content,
    }))
  }

  // Handle feedback changes
  const handleFeedbackChange = (sectionKey, feedback, priority = 3) => {
    if (feedback && feedback.trim()) {
      setSectionFeedback((prev) => ({
        ...prev,
        [sectionKey]: { feedback, priority },
      }))
    } else {
      setSectionFeedback((prev) => {
        const newFeedback = { ...prev }
        delete newFeedback[sectionKey]
        return newFeedback
      })
    }
  }

  // Multi-section feedback handlers
  const handleMultiSectionToggle = (sectionKey, checked) => {
    // Do not allow multi-feedback selection for restricted sections
    if (restrictedSections.has(sectionKey)) return
    setSelectedSectionsForFeedback((prev) => {
      const updated = { ...prev }
      if (checked) {
        updated[sectionKey] = true
        // Only clear custom content for this section (keep individual AI feedback)
        setCustomContent((prevCustom) => {
          const newCustom = { ...prevCustom }
          delete newCustom[sectionKey]
          return newCustom
        })
        // Clear custom edit mode, but allow feedback mode to remain
        setSectionEditModes((prevModes) => {
          const newModes = { ...prevModes }
          if (newModes[sectionKey] === "custom") {
            delete newModes[sectionKey]
          }
          return newModes
        })
      } else {
        delete updated[sectionKey]
      }
      return updated
    })
  }
  // Reset session flags on Start Over
  useEffect(() => {
    if (!showStartOverConfirm) return
  }, [showStartOverConfirm])

  const resetRestrictedModals = () => {
    const initial = { why: false, cta: false }
    setDismissedRestrictedModals(initial)
    try { sessionStorage.setItem('dismissedRestrictedModals', JSON.stringify(initial)) } catch (_) {}
  }

  // Multi-section feedback handlers

  const isUsingMultiFeedback = Object.keys(selectedSectionsForFeedback).length > 0 || showMultiFeedback

  // Validate and submit changes
  const validateAndSubmit = () => {
    const hasCustomEdits = Object.keys(customContent).some(
      (key) => customContent[key] && customContent[key].toString().trim(),
    )
    const hasFeedback = Object.keys(sectionFeedback).length > 0
    const hasMultiFeedback = multiFeedbackText.trim() && Object.keys(selectedSectionsForFeedback).length > 0

    if (!hasCustomEdits && !hasFeedback && !hasMultiFeedback) {
      setValidationError("Please provide either custom edits or feedback for at least one section.")
      return false
    }

    setValidationError("")
    return true
  }

  // Sequential submission: Custom edits first, then feedback
  const handleSubmitChanges = async () => {
    if (!validateAndSubmit()) return

    setSubmitting(true)
    try {
      // **SOLUTION**: Use baselineJobId for edit operations if available (after merges),
      // otherwise fall back to current case study job ID
      let finalJobId = baselineJobId || caseStudy?.metadata?.job_id
      
      // **DEBUG LOGGING**: Track job ID flow for debugging merge issues
      console.log("🔍 Job ID Tracking:", {
        baselineJobId,
        caseStudyJobId: caseStudy?.metadata?.job_id,
        finalJobId,
        hasCustomEdits: Object.keys(customContent).some(key => customContent[key] && customContent[key].toString().trim())
      })

      // Step 1: Apply custom edits first if any exist
      const hasCustomEdits = Object.keys(customContent).some(
        (key) => customContent[key] && customContent[key].toString().trim(),
      )

      // Initialize merged case study data variable
      let mergedCaseStudyData = null

      if (hasCustomEdits) {
        // Apply custom edits first

        // Create section selections for merge
        const sectionSelections = {}
        const finalCustomContent = {}

        sections.forEach((section) => {
          const hasCustomContent = customContent[section.key] && customContent[section.key].toString().trim()

          if (hasCustomContent) {
            sectionSelections[section.key] = "custom"

            // For custom content, send as strings (ready-to-use markdown)
            // The specialized editors (ChallengesEditor, BusinessOutcomesEditor) already convert to markdown strings
            finalCustomContent[section.key] = customContent[section.key]
          } else if (section.exists) {
            sectionSelections[section.key] = "revised"
          } else {
            sectionSelections[section.key] = "revised"
          }
        })

        if (!finalJobId) {
          throw new Error("No job ID available for applying changes")
        }

        // Apply custom edits via merge endpoint
        console.log("🔧 Calling onMergeSections with jobId:", finalJobId)
        const mergeResult = await onMergeSections(finalJobId, sectionSelections, finalCustomContent)

        // Update job ID to the new merged case study and store merged case study data
        if (mergeResult?.job_id) {
          console.log("✅ Merge successful, updating finalJobId:", finalJobId, "→", mergeResult.job_id)
          finalJobId = mergeResult.job_id
          mergedCaseStudyData = mergeResult.case_study // Store the updated case study data
          // Custom edits applied successfully
        } else {
          console.warn("⚠️ Merge result missing job_id:", mergeResult)
        }
      }

      // Step 2: Process multi-section feedback and combine with existing feedback
      const hasMultiFeedback = multiFeedbackText.trim() && Object.keys(selectedSectionsForFeedback).length > 0
      let finalSectionFeedback = { ...sectionFeedback }

      if (hasMultiFeedback) {
        // Combine multi-feedback with existing individual feedback for each selected section
        Object.keys(selectedSectionsForFeedback).forEach((sectionKey) => {
          const existingFeedback = sectionFeedback[sectionKey]?.feedback || ""
          const combinedFeedback = existingFeedback
            ? `${multiFeedbackText.trim()}\n\nAdditional specific feedback: ${existingFeedback}`
            : multiFeedbackText.trim()

          finalSectionFeedback[sectionKey] = {
            feedback: combinedFeedback,
            priority: Math.max(3, sectionFeedback[sectionKey]?.priority || 3), // Use higher priority
          }
        })
      }

      // Step 3: Submit feedback for revision if any exists
      const hasFeedback = Object.keys(finalSectionFeedback).length > 0

      if (hasFeedback) {
        // Submit revision feedback with combined multi-section + individual feedback
        const feedbackData = {
          section_feedback: Object.entries(finalSectionFeedback).map(([sectionKey, data]) => ({
            section_name: sectionKey,
            feedback: data.feedback,
            priority: data.priority,
          })),
          // NOTE: finished_sections should NOT be included here - it will be added at root level by useApiCalls
        }

        // Pass the merged case study data to ensure revision uses updated content
        await onFeedback(feedbackData, mergedCaseStudyData)
        // Revision feedback submitted successfully
      }

      // Clear form data
      setCustomContent({})
      setSectionFeedback({})
      setSectionEditModes({})
      setExpandedSections({})
      // Clear multi-feedback state
      setSelectedSectionsForFeedback({})
      setMultiFeedbackText("")
      setShowMultiFeedback(false)

      // All changes applied successfully
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("❌ Failed to submit changes:", error)
      }
      setValidationError("Failed to submit changes. Please try again.")
    } finally {
      setSubmitting(false)
    }
  }

  // Specialized formatters for complex nested objects
  const formatKeyMetrics = (metrics) => {
    if (!Array.isArray(metrics)) return ""
    return metrics.map(metric => 
      metric && typeof metric === "object" 
        ? `**${metric.title || ""}**: ${metric.description || ""}`
        : `- ${metric}`
    ).join("\n\n")
  }

  const formatOutcomes = (outcomes) => {
    if (!Array.isArray(outcomes)) return ""
    return outcomes.map(outcome => 
      outcome && typeof outcome === "object"
        ? `**${outcome.title || ""}**\n${outcome.description || ""}`
        : `- ${outcome}`
    ).join("\n\n")
  }

  const formatTestimonial = (testimonial) => {
    if (!testimonial || typeof testimonial !== "object") return ""
    const { quote = "", author_name = "", author_title = "", company = "" } = testimonial
    return `"${quote}"\n\n— ${author_name}, ${author_title}, ${company}`.trim()
  }

  const formatPartnershipDetails = (partnershipDetails) => {
    if (!partnershipDetails || typeof partnershipDetails !== "object") return ""
    const { client_overview = "", partnership_details = [] } = partnershipDetails
    
    let result = client_overview
    if (Array.isArray(partnership_details) && partnership_details.length > 0) {
      const highlights = partnership_details.map(detail => 
        detail && typeof detail === "object"
          ? `**${detail.title || ""}**: ${detail.description || ""}`
          : `- ${detail}`
      ).join("\n\n")
      result += result ? `\n\n${highlights}` : highlights
    }
    return result
  }

  const formatCallToAction = (callToAction) => {
    if (!callToAction || typeof callToAction !== "object") return ""
    const { title = "", description = "" } = callToAction
    return `**${title}**\n\n${description}`.trim()
  }

  // Deep comparison function for complex objects
  const areObjectsEqual = (obj1, obj2) => {
    if (obj1 === obj2) return true
    if (!obj1 || !obj2) return obj1 === obj2
    if (typeof obj1 !== typeof obj2) return false
    
    if (Array.isArray(obj1) && Array.isArray(obj2)) {
      if (obj1.length !== obj2.length) return false
      return obj1.every((item, index) => areObjectsEqual(item, obj2[index]))
    }
    
    if (typeof obj1 === "object" && typeof obj2 === "object") {
      const keys1 = Object.keys(obj1).sort()
      const keys2 = Object.keys(obj2).sort()
      if (keys1.length !== keys2.length) return false
      if (!keys1.every(key => keys2.includes(key))) return false
      return keys1.every(key => areObjectsEqual(obj1[key], obj2[key]))
    }
    
    return obj1 === obj2
  }

  // Enhanced content comparison that handles both formatted strings and object comparison
  const isContentIdentical = (sectionKey, originalContent, revisedContent) => {
    // First try the formatted string comparison
    if (originalContent === revisedContent && originalContent) {
      return true
    }
    
    // For complex objects, also compare the raw data
    if (!originalCaseStudy?.data || !caseStudy?.data) return false
    
    const originalData = originalCaseStudy.data[sectionKey]
    const revisedData = caseStudy.data[sectionKey]
    
    return areObjectsEqual(originalData, revisedData)
  }

  // Render markdown content for merge view
  const renderMarkdownContent = (content) => {
    if (!content) return <em className="text-gray-500">No content</em>
    
    return (
      <div className="text-sm text-gray-700">
        <ReactMarkdown 
          components={{
            // Override default styles to match the merge view
            p: ({children}) => <p className="mb-2 last:mb-0 text-sm text-gray-700">{children}</p>,
            strong: ({children}) => <strong className="font-semibold text-gray-800">{children}</strong>,
            ul: ({children}) => <ul className="list-disc list-inside space-y-1 ml-2">{children}</ul>,
            li: ({children}) => <li className="text-sm text-gray-700">{children}</li>,
            // Remove default margins from headings if they appear
            h1: ({children}) => <h1 className="font-semibold text-gray-800 mb-1 text-sm">{children}</h1>,
            h2: ({children}) => <h2 className="font-semibold text-gray-800 mb-1 text-sm">{children}</h2>,
            h3: ({children}) => <h3 className="font-semibold text-gray-800 mb-1 text-sm">{children}</h3>,
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
    )
  }

  // Helper functions for merge view
  const getOriginalSectionContent = (sectionKey) => {
    try {
      if (!originalCaseStudy?.data) return ""
      const content = originalCaseStudy.data[sectionKey]

      // Handle null or undefined content
      if (content === null || content === undefined) return ""

      // Handle specialized complex objects first
      switch (sectionKey) {
        case "key_metrics":
          return formatKeyMetrics(content)
        case "outcomes":
          return formatOutcomes(content)
        case "testimonial":
          return formatTestimonial(content)
        case "partnership_details":
          return formatPartnershipDetails(content)
        case "call_to_action":
          return formatCallToAction(content)
      }

      // Handle arrays (for simple lists like challenges, objectives)
      if (Array.isArray(content)) {
        return content.map((item) => `- ${item}`).join("\n")
      }

      // Handle simple objects with the existing logic
      if (typeof content === "object" && content !== null) {
        return Object.entries(content)
          .map(([key, value]) => {
            if (Array.isArray(value)) {
              if (key.toLowerCase() === "bullet_points") {
                return value.map((item) => `- ${item}`).join("\n")
              }
              return `${key}:\n${value.map((item) => `- ${item}`).join("\n")}`
            }
            // Don't add key prefix for explanation field - just return the content directly
            if (key.toLowerCase() === "explanation") {
              return value
            }
            // Format positive business outcomes subheaders with proper styling
            if (typeof value === "string" && value.length > 0) {
              return `${key}\n${value}`
            }
            return `${key}: ${value}`
          })
          .join("\n\n")
      }

      return typeof content === "string" ? content : ""
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error extracting original section content:", error)
      }
      return ""
    }
  }

  const getRevisedSectionContent = (sectionKey) => {
    try {
      if (!caseStudy?.data) return ""
      const content = caseStudy.data[sectionKey]

      // Handle null or undefined content
      if (content === null || content === undefined) return ""

      // Handle specialized complex objects first
      switch (sectionKey) {
        case "key_metrics":
          return formatKeyMetrics(content)
        case "outcomes":
          return formatOutcomes(content)
        case "testimonial":
          return formatTestimonial(content)
        case "partnership_details":
          return formatPartnershipDetails(content)
        case "call_to_action":
          return formatCallToAction(content)
      }

      // Handle arrays (for simple lists like challenges, objectives)
      if (Array.isArray(content)) {
        return content.map((item) => `- ${item}`).join("\n")
      }

      // Handle simple objects with the existing logic
      if (typeof content === "object" && content !== null) {
        return Object.entries(content)
          .map(([key, value]) => {
            if (Array.isArray(value)) {
              if (key.toLowerCase() === "bullet_points") {
                return value.map((item) => `- ${item}`).join("\n")
              }
              return `${key}:\n${value.map((item) => `- ${item}`).join("\n")}`
            }
            // Don't add key prefix for explanation field - just return the content directly
            if (key.toLowerCase() === "explanation") {
              return value
            }
            // Format positive business outcomes subheaders with proper styling
            if (typeof value === "string" && value.length > 0) {
              return `${key}\n${value}`
            }
            return `${key}: ${value}`
          })
          .join("\n\n")
      }

      return typeof content === "string" ? content : ""
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error extracting revised section content:", error)
      }
      return ""
    }
  }

  // Get the title of the case study for display
  const getCaseStudyTitle = (data) => {
    if (!data) return "Case Study"
    const title = data.title
    if (!title) return "Case Study"

    if (typeof title === "string") {
      return title.replace(/^"(.*)"$/, "$1").trim()
    }

    return "Case Study"
  }

  // Handle merge submission
  const handleMergeSubmit = async () => {
    // For AI revisions, prefer the revision job ID so backend has both original and revised
    // Otherwise use baselineJobId; fall back to original or current job IDs
    const jobIdToUse = revisionJobId || baselineJobId || originalCaseStudy?.metadata?.job_id || caseStudy?.metadata?.job_id
    
    if (!jobIdToUse || !onMergeSections) {
      if (import.meta.env.DEV) {
        console.error("Missing job ID or merge function", { revisionJobId, baselineJobId, caseStudyJobId: caseStudy?.metadata?.job_id })
      }
      return
    }

    setSubmitting(true)
    setValidationError("")

    try {
      // Ensure all sections have selections, default to 'revised'
      const finalSelections = {}
      sections.forEach((section) => {
        finalSelections[section.key] = sectionSelections[section.key] || "revised"
      })
      
      // For merge custom content, send as strings (ready-to-use markdown)
      // The specialized editors already convert to markdown strings
      const formattedMergeCustomContent = { ...mergeCustomContent }
      
      if (import.meta.env.DEV) {
        console.log("🔍 Debug merge selections:", {
          titleSelection: finalSelections.title,
          originalTitle: originalCaseStudy?.data?.title,
          currentTitle: caseStudy?.data?.title,
          finalSelections,
          jobIdToUse,
          mergeCustomContent: formattedMergeCustomContent
        })
      }

      const result = await onMergeSections(jobIdToUse, finalSelections, formattedMergeCustomContent)

      if (result && result.case_study) {
        // Merge completed successfully
        setMergeCompleted(true)
        // Switch back to unified view after successful merge
        setViewMode("unified")
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Merge failed:", error)
      }
      setValidationError("Failed to create merged case study. Please try again.")
    } finally {
      setSubmitting(false)
    }
  }

  if (!caseStudy) {
    return (
      <div className="text-center py-12 text-gray-500">No case study to preview yet. Upload a file to get started!</div>
    )
  }

  // Get case study titles for display
  const originalTitle = originalCaseStudy ? getCaseStudyTitle(originalCaseStudy.data) : "Previous"
  const revisedTitle = getCaseStudyTitle(caseStudy.data)

  return (
    <Tooltip.Provider delayDuration={400}>
    <>
    <div className="space-y-6">
      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3 justify-between">
        <div className="flex gap-3">
          <button
            onClick={handleHtmlPreview}
            className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors flex items-center gap-2"
          >
            <span>⭐</span> Finalize Study
          </button>
          <button
            onClick={onDownload}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors flex items-center gap-2"
          >
            <span>📥</span> Save Progress
          </button>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowStartOverConfirm(true)}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors flex items-center gap-2"
          >
            <span>🔄</span> Start Over
          </button>
        </div>
      </div>

      {/* View Mode Tabs - Show merge option only when we have versions to compare */}
      {hasVersions && originalCaseStudy && caseStudy && (
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8">
            <button
              onClick={() => handleNavigationWithConfirmation("unified")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                viewMode === "unified"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              📄 Case Study Draft
            </button>
            <button
              onClick={() => handleNavigationWithConfirmation("merge")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                viewMode === "merge"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              🔀 Compare & Merge
            </button>
          </nav>
        </div>
      )}

      {/* Unified Case Study View */}
      {viewMode === "unified" && (
        <div className="bg-white rounded-lg shadow-md">
          {/* Full-screen modal for restricted sections */}
          {showRestrictedModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="absolute inset-0 bg-black bg-opacity-60"></div>
              <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-yellow-600 text-xl">⚠️</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {restrictedModalType === 'why' ? 'Restricted: Why RISCPoint' : 'Restricted: Call To Action'}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Do you have permission to edit this section? It is pre-written and you must get prior approval before changing it.
                    </p>
                  </div>
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowRestrictedModal(false)
                      const updated = { ...dismissedRestrictedModals }
                      if (restrictedModalType) updated[restrictedModalType] = true
                      setDismissedRestrictedModals(updated)
                      try { sessionStorage.setItem('dismissedRestrictedModals', JSON.stringify(updated)) } catch (_) {}
                    }}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="border-b px-6 py-4" ref={caseStudyHeaderRef}>
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-gray-800">Case Study Draft</h2>
                <div className="text-sm text-gray-500 mt-1">
                  Generated on {new Date().toLocaleDateString()} • {sections.filter((s) => s.exists).length} sections
                </div>
              </div>
              
              {/* AI Mass Revision Toggle - inline with header */}
              <button
                onClick={() => setShowMultiFeedback(!showMultiFeedback)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                  showMultiFeedback || Object.keys(selectedSectionsForFeedback).length > 0
                    ? "bg-purple-300 text-purple-800"
                    : "bg-purple-100 text-purple-700 border border-purple-200 hover:bg-purple-200"
                }`}
              >
                🤖 AI Mass Revision
                {Object.keys(selectedSectionsForFeedback).length > 0 && (
                  <span className="bg-purple-200 text-purple-800 text-xs px-2 py-1 rounded-full">
                    {Object.keys(selectedSectionsForFeedback).length}
                  </span>
                )}
              </button>
            </div>

            {/* Apply Changes Banner - Shows here initially */}
            {(Object.keys(customContent).some((key) => customContent[key] && customContent[key].toString().trim()) ||
              Object.keys(sectionFeedback).length > 0 ||
              Object.keys(selectedSectionsForFeedback).length > 0) && (
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4" ref={applyBannerRef}>
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="text-sm font-medium text-blue-800">Changes Ready to Apply</h4>
                    <p className="text-xs text-blue-600 mt-1">
                      {
                        Object.keys(customContent).filter(
                          (key) => customContent[key] && customContent[key].toString().trim(),
                        ).length
                      }{" "}
                      Custom Edits • {Object.keys(sectionFeedback).length} AI Revision Sections
                    </p>
                  </div>
                  <button
                    onClick={() => setShowSubmitConfirm(true)}
                    disabled={submitting}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${submitting ? "bg-gray-400 text-gray-600 cursor-not-allowed" : "bg-blue-500 text-white hover:bg-blue-600"}`}
                  >
                    {submitting ? "Processing..." : "Apply Changes"}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* AI Suggestions (if available) */}
          {caseStudy.data?.ai_suggestions && caseStudy.data.ai_suggestions.length > 0 && (
            <div className="bg-yellow-50 border-b border-yellow-200 p-4">
              <h3 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                <span>💡</span> AI Suggestions for Improvement
              </h3>
              <ul className="space-y-1">
                {caseStudy.data.ai_suggestions.map((suggestion, index) => (
                  <li key={index} className="text-sm text-gray-700 flex items-start gap-2">
                    <span className="text-yellow-600 mt-0.5">•</span>
                    <span>{suggestion}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Multi-Section Feedback Panel */}
          {showMultiFeedback && (
            <div className="bg-purple-50 border-b border-purple-200 p-6">
              <h3 className="font-medium text-purple-800 mb-4 flex items-center gap-2">
                <span>🤖</span> Provide Feedback for Multiple Sections
              </h3>
              {/*<p className="text-sm text-purple-700 mb-4">*/}
              {/*  Select sections below and provide feedback that will be applied to all selected sections.*/}
              {/*</p>*/}

              <div className="space-y-4">
                {/* Feedback Text Input */}
                <div>
                  <textarea
                    value={multiFeedbackText}
                    onChange={(e) => setMultiFeedbackText(e.target.value)}
                    rows={4}
                    className="w-full border border-purple-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-vertical"
                    placeholder="Enter feedback that will apply to all selected sections below..."
                  />
                </div>

                {/* Section Selection Grid */}
                <div>
                  <h4 className="font-medium text-purple-800 mb-2">Apply this feedback to selected sections:</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {sections
                      .filter((section) => section.exists && section.key !== "testimonial" && !restrictedSections.has(section.key))
                      .map((section) => (
                        <label
                          key={section.key}
                          className={`flex items-center space-x-2 p-3 rounded-md border cursor-pointer transition-colors ${
                            selectedSectionsForFeedback[section.key]
                              ? "bg-purple-100 border-purple-300"
                              : "bg-white border-gray-300 hover:bg-purple-50"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedSectionsForFeedback[section.key] || false}
                            onChange={(e) => handleMultiSectionToggle(section.key, e.target.checked)}
                            className="text-purple-600 focus:ring-purple-500 rounded"
                          />
                          <span className="text-sm text-gray-700 truncate">{getDynamicSectionName(section, section.content)}</span>
                        </label>
                      ))}
                  </div>
                </div>

                {/* Action Buttons */}
                {/*<div className="text-sm text-gray-600 flex items-center">*/}
                {/*  "Apply Changes" will add this feedback to all selected sections.*/}
                {/*</div>*/}
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => {
                      setShowMultiFeedback(false)
                      setMultiFeedbackText("")
                      setSelectedSectionsForFeedback({})
                      setValidationError("")
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>

                </div>
              </div>
            </div>
          )}

          {/* Sectioned Case Study Content */}
          <div className="divide-y divide-gray-100">
            {sections.map((section) => (
                <div key={section.key} className="group">
                  {/* Section Header with Toggle */}
                  <div
                    className="flex items-center justify-between p-6 hover:bg-gray-50 cursor-pointer"
                    onClick={() => toggleSection(section.key)}
                  >
                    <div className="flex-1">
                      {section.exists ? (
                        // Existing section content
                        <>
                          {/* For title and subtitle, don't show separate heading since it's in the markdown */}
                          {section.key === "title" || section.key === "subtitle" ? (
                            <div
                              className={`prose prose-sm max-w-none ${
                                section.key === "title"
                                  ? "text-2xl font-bold text-gray-900"
                                  : "text-xl text-gray-800"
                              }`}
                            >
                              <DraftMarkdownSnippet>
                                {typeof section.content === "string"
                                  ? section.content.replace(/^"(.*)"$/, "$1")
                                  : String(section.content ?? "")}
                              </DraftMarkdownSnippet>
                            </div>
                          ) : (
                            <>
                              <h3 className="text-lg font-medium text-gray-800 mb-2">
                                {getDynamicSectionName(section, section.content)}
                              </h3>
                              {/* For other sections, don't include the section label in the markdown content */}
                              <div className="prose prose-sm max-w-none text-gray-600">
                                {section.key === "outcomes" ? (
                                  <div>{renderOutcomes(section.content)}</div>
                                ) : section.key === "key_metrics" ? (
                                  <div>{renderKeyMetrics(section.content)}</div>
                                ) : section.key === "testimonial" ? (
                                  <div>{renderTestimonial(section.content)}</div>
                                ) : section.key === "partnership_details" ? (
                                  <div>{renderPartnershipDetails(section.content)}</div>
                                ) : section.key === "call_to_action" ? (
                                  <div>{renderCallToAction(section.content)}</div>
                                ) : (
                                  <DraftMarkdownSnippet className="max-w-none text-gray-600">
                                    {renderSectionContent(section.content, section.key)}
                                  </DraftMarkdownSnippet>
                                )}
                              </div>
                            </>
                          )}
                        </>
                      ) : (
                        // Missing section content
                        <>
                          <h3 className="text-lg font-medium text-gray-800 mb-2">
                            {section.label}
                          </h3>
                          <div className="text-gray-500 italic">
                            {section.key === "testimonial" ? (
                              <span>
                                💬 Client testimonial not included. Click to add via custom edit with Quote and Person fields.
                              </span>
                            ) : (
                              <span>
                                Click to expand and add this section.
                              </span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      {/* Missing Section Indicator */}
                      {!section.exists && (
                        <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded font-medium">
                          Missing
                        </span>
                      )}
                      
                      {/* Edit Mode Indicators */}
                      {selectedSectionsForFeedback[section.key] && (
                        <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                          AI Mass Edit
                        </span>
                      )}
                      {sectionEditModes[section.key] === "custom" && !selectedSectionsForFeedback[section.key] && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Custom Edit</span>
                      )}
                      {sectionEditModes[section.key] === "feedback" && !selectedSectionsForFeedback[section.key] && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">AI Revision</span>
                      )}
                      {sectionFeedback[section.key] && !selectedSectionsForFeedback[section.key] && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Feedback Ready</span>
                      )}
                      {/* Expand/Collapse Icon */}
                      <button className="text-gray-400 hover:text-gray-600 transition-colors">
                        <svg
                          className={`w-5 h-5 transition-transform ${expandedSections[section.key] ? "rotate-180" : ""}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Expanded Section Controls */}
                  {expandedSections[section.key] && (
                    <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                      <div className="space-y-4">
                        {/* Multi-feedback info */}
                        {selectedSectionsForFeedback[section.key] && (
                          <div className="bg-purple-50 border border-purple-200 rounded-md p-3">
                            <div className="flex items-center gap-2">
                              <span className="text-purple-600">🤖</span>
                              <p className="text-sm text-purple-800">
                                This section is selected for AI mass edit. Custom edits are disabled, but
                                you can still add additional AI feedback below.
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Edit Mode Selection */}
                        <div className="space-y-3">
                          {!section.exists && section.key === "testimonial" && (
                            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                              <div className="flex items-center gap-2">
                                <span className="text-blue-600">💬</span>
                                <p className="text-sm text-blue-800">
                                  Client testimonials can only be added via custom edit with Quote and Person fields.
                                </p>
                              </div>
                            </div>
                          )}
                          
                          <div className="flex gap-4">
                            <button
                              onClick={() => setSectionEditMode(section.key, "custom")}
                              disabled={selectedSectionsForFeedback[section.key]}
                              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                selectedSectionsForFeedback[section.key]
                                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                                  : sectionEditModes[section.key] === "custom"
                                    ? "bg-blue-500 text-white"
                                    : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                              }`}
                            >
                              ✏️ {section.exists ? "Custom Edit" : "Add Content"}
                            </button>
                            {section.key !== "testimonial" && !restrictedSections.has(section.key) && (
                              <button
                                onClick={() => setSectionEditMode(section.key, "feedback")}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                  sectionEditModes[section.key] === "feedback"
                                    ? "bg-green-500 text-white"
                                    : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                                }`}
                              >
                                🤖 {section.exists ? "AI Revision" : "Request via AI"}
                                {selectedSectionsForFeedback[section.key] && (
                                  <span className="ml-1 text-xs">(Additional)</span>
                                )}
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Custom Edit Interface */}
                        {sectionEditModes[section.key] === "custom" && (
                          <div className="bg-white rounded-lg border border-gray-200 p-4">
                            <h4 className="text-sm font-medium text-gray-800 mb-3">Edit {section.label}</h4>
                            <UnifiedSectionEditor
                              section={section}
                              mode="edit"
                              currentContent={section.content}
                              customContent={customContent}
                              onCustomContentChange={handleCustomContentChange}
                            />
                          </div>
                        )}

                        {/* Feedback Interface */}
                        {sectionEditModes[section.key] === "feedback" && !restrictedSections.has(section.key) && (
                          <div className="bg-white rounded-lg border border-gray-200 p-4">
                            <h4 className="text-sm font-medium text-gray-800 mb-3">
                              {selectedSectionsForFeedback[section.key]
                                ? `Additional AI Feedback for ${section.label}`
                                : `Provide AI Feedback for ${section.label}`}
                            </h4>
                            {selectedSectionsForFeedback[section.key] && (
                              <div className="bg-purple-50 border border-purple-200 rounded-md p-2 mb-3">
                                <p className="text-xs text-purple-700">
                                  This will be combined with the multi-section feedback you provided.
                                </p>
                              </div>
                            )}
                            <div className="space-y-3">
                              <textarea
                                value={sectionFeedback[section.key]?.feedback || ""}
                                onChange={(e) =>
                                  handleFeedbackChange(
                                    section.key,
                                    e.target.value,
                                    sectionFeedback[section.key]?.priority || 3,
                                  )
                                }
                                rows={4}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-vertical"
                                placeholder={`Describe what changes you'd like to see in the ${section.label} section`}
                              />
                              <div className="flex items-center gap-2">
                                <label className="text-sm font-medium text-gray-700">Priority:</label>
                                <select
                                  value={sectionFeedback[section.key]?.priority || 3}
                                  onChange={(e) =>
                                    handleFeedbackChange(
                                      section.key,
                                      sectionFeedback[section.key]?.feedback || "",
                                      Number.parseInt(e.target.value, 10),
                                    )
                                  }
                                  className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-green-500"
                                >
                                  <option value="1">1 - Low</option>
                                  <option value="2">2 - Medium-Low</option>
                                  <option value="3">3 - Normal</option>
                                  <option value="4">4 - High</option>
                                  <option value="5">5 - Critical</option>
                                </select>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}

          </div>

          {/* Validation Error Section - only shows when there's an error */}
          {validationError && (
            <div className="border-t border-gray-200 p-6 bg-red-50">
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <div className="flex items-center">
                  <span className="text-red-400 mr-2">⚠️</span>
                  <p className="text-sm text-red-700">{validationError}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Merge Sections View */}
      {viewMode === "merge" && originalCaseStudy && caseStudy && (
        <div className="space-y-6">
          {/* Validation check for required data */}
          {!originalCaseStudy?.data || !caseStudy?.data ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h3 className="text-lg font-bold text-red-900 mb-3">⚠️ Merge Not Available</h3>
              <p className="text-red-800 text-sm">
                {!originalCaseStudy?.data && !caseStudy?.data
                  ? "Both original and revised case studies are missing data required for merging."
                  : !originalCaseStudy?.data
                    ? "Previous case study data is missing. Please ensure you have a complete revision workflow."
                    : "Revised case study data is missing. Please complete the revision process first."}
              </p>
              <button
                onClick={() => setViewMode("unified")}
                className="mt-4 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
              >
                Return to Case Study
              </button>
            </div>
          ) : (
            <>
              <div className="bg-indigo-50 border border-indigo-300 rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-bold text-indigo-900 mb-3">🔀 Compare & Merge Versions</h3>
                <p className="text-indigo-800 text-sm mb-4">
                  Select which version of each section you want to include in the final case study.
                </p>
                
                {/* Post-merge lock message */}
                {mergeCompleted && (
                  <div className="bg-green-50 border border-green-300 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-green-600">✓</span>
                      <h4 className="font-medium text-green-900">Selected Version Created</h4>
                    </div>
                    <p className="text-green-800 text-sm">
                      You have successfully created your selected version. To make further changes, 
                      use manual edits in the Case Study tab or request an AI revision.
                    </p>
                  </div>
                )}


                <div className="grid grid-cols-2 gap-4 text-xs font-medium">
                  <div className="flex items-center gap-2 bg-blue-100 p-2 rounded-md">
                    <div className="w-3 h-3 rounded-full bg-blue-500 flex-shrink-0"></div>
                    <span className="text-blue-800 truncate">
                      Previous Version
                    </span>
                  </div>
                  <div className="flex items-center gap-2 bg-green-100 p-2 rounded-md">
                    <div className="w-3 h-3 rounded-full bg-green-500 flex-shrink-0"></div>
                    <span className="text-green-800 truncate">Current ({revisedTitle})</span>
                  </div>
                </div>
              </div>

              {/* Save New Case Study Button */}
              {!mergeCompleted && (
                <div className="flex justify-center" ref={mergeButtonRef}>
                  <button
                    onClick={handleMergeSubmit}
                    disabled={submitting}
                    className={`px-6 py-3 rounded-md font-medium transition-colors ${
                      submitting
                        ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                        : "bg-indigo-500 text-white hover:bg-indigo-600"
                    }`}
                  >
                    {submitting ? "Saving New Case Study..." : "Save New Case Study"}
                  </button>
                </div>
              )}

              <div className={`space-y-6 ${mergeCompleted ? 'opacity-50 pointer-events-none' : ''}`}>
                {sections.map((section) => {
                  try {
                    const originalContent = getOriginalSectionContent(section.key)
                    const revisedContent = getRevisedSectionContent(section.key)
                    const selection = sectionSelections[section.key] || (section.exists ? "revised" : "revised")

                    // Check if content is identical (using enhanced comparison for complex objects)
                    const contentIsIdentical = isContentIdentical(section.key, originalContent, revisedContent)

                    // Check if section exists in either version
                    const hasOriginal = originalContent && originalContent.trim()
                    const hasRevised = revisedContent && revisedContent.trim()
                    const hasAnyContent = hasOriginal || hasRevised

                    // Skip sections that don't exist in either version
                    if (!hasAnyContent) {
                      return (
                        <div key={section.key} className="bg-yellow-50 rounded-lg border border-yellow-200 p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-yellow-600">⚠️</span>
                            <h4 className="font-medium text-gray-800">{section.label}</h4>
                            <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                              Missing from both versions
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">
                            This section is not available in either the original or revised version.
                          </p>
                        </div>
                      )
                    }

                    // Get dynamic section names
                    const originalSectionName = getDynamicSectionName(section, originalCaseStudy?.data?.[section.key])
                    const revisedSectionName = getDynamicSectionName(section, caseStudy?.data?.[section.key])

                    // Create a more descriptive section title for the merge view
                    let sectionTitle = section.label
                    if (section.key === "title" || section.key === "subtitle") {
                      // For title/subtitle, show just the actual content (no prefix)
                      const currentContent = hasRevised
                        ? revisedSectionName
                        : hasOriginal
                          ? originalSectionName
                          : section.label
                      sectionTitle = currentContent
                    } else if (section.key === "key_metrics" || section.key === "objectives") {
                      // For list sections, indicate they're lists
                      sectionTitle = `${section.label} (List)`
                    }

                    return (
                      <div key={section.key} className="bg-white rounded-lg shadow-md border border-gray-200">
                        <div className="border-b px-6 py-4 bg-gray-50">
                          <div className="flex items-center justify-between">
                            <h4 className="text-lg font-medium text-gray-800">{sectionTitle}</h4>
                          </div>
                        </div>

                        <div className="p-6 space-y-4">
                          {contentIsIdentical ? (
                            // Show simplified view for identical content
                            <>
                              {/*<div className="text-center text-sm text-gray-600 mb-4">*/}
                              {/*  Content is identical in both versions*/}
                              {/*</div>*/}
                              <div
                                className="border-2 border-gray-300 rounded-lg p-4 bg-gray-50 cursor-pointer hover:border-gray-400 transition-colors"
                                onClick={() =>
                                  setSectionSelections((prev) => ({
                                    ...prev,
                                    [section.key]: "revised",
                                  }))
                                }
                              >
                                <div className="text-sm font-medium text-gray-800 mb-2 flex items-center gap-2">
                                  <span className="text-xs bg-gray-200 text-gray-800 px-2 py-1 rounded">Selected</span>
                                </div>
                                <div>
                                  {renderMarkdownContent(originalContent)}
                                </div>
                              </div>
                            </>
                          ) : (
                            // Show comparison for different content
                            <div
                              className={`grid gap-4 ${hasOriginal && hasRevised ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"}`}
                            >
                              {/* Previous Content */}
                              {hasOriginal && (
                                <div
                                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                                    selection === "original"
                                      ? "border-blue-500 bg-blue-50 shadow-md"
                                      : "border-blue-200 bg-blue-50 hover:border-blue-400 hover:shadow-sm"
                                  }`}
                                  onClick={() =>
                                    setSectionSelections((prev) => ({
                                      ...prev,
                                      [section.key]: "original",
                                    }))
                                  }
                                >
                                  <h5 className="text-sm font-medium text-blue-800 mb-2 flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full bg-blue-500 flex-shrink-0"></div>
                                    <span className="truncate">Previous Version</span>
                                    {selection === "original" && (
                                      <span className="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded flex-shrink-0">
                                        Selected
                                      </span>
                                    )}
                                  </h5>
                                  <div>
                                    {renderMarkdownContent(originalContent)}
                                  </div>
                                </div>
                              )}

                              {/* Revised Content */}
                              {hasRevised && (
                                <div
                                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                                    selection === "revised"
                                      ? "border-green-500 bg-green-50 shadow-md"
                                      : "border-green-200 bg-green-50 hover:border-green-400 hover:shadow-sm"
                                  }`}
                                  onClick={() =>
                                    setSectionSelections((prev) => ({
                                      ...prev,
                                      [section.key]: "revised",
                                    }))
                                  }
                                >
                                  <h5 className="text-sm font-medium text-green-800 mb-2 flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full bg-green-500 flex-shrink-0"></div>
                                    <span className="truncate">Revised Version</span>
                                    {selection === "revised" && (
                                      <span className="text-xs bg-green-200 text-green-800 px-2 py-1 rounded flex-shrink-0">
                                        Selected
                                      </span>
                                    )}
                                  </h5>
                                  <div>
                                    {renderMarkdownContent(revisedContent)}
                                  </div>
                                </div>
                              )}

                              {/* Single version notice */}
                              {hasOriginal && !hasRevised && (
                                <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                                  <p className="text-sm text-yellow-800">
                                    ℹ️ This section only exists in the previous version.
                                  </p>
                                </div>
                              )}

                              {!hasOriginal && hasRevised && (
                                <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                                  <p className="text-sm text-yellow-800">
                                    ℹ️ This section was added in the current version.
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  } catch (error) {
                    if (import.meta.env.DEV) {
                      console.error(`Error rendering section ${section.key}:`, error)
                    }
                    return (
                      <div key={section.key} className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <h4 className="text-red-800 font-medium">Error loading {section.label}</h4>
                        <p className="text-red-600 text-sm">Unable to load content for this section.</p>
                      </div>
                    )
                  }
                })}
              </div>

            </>
          )}
        </div>
      )}

      {/* Version Info Banner */}
      {versionInfo && (
        <div
          className={`rounded-lg p-4 border ${
            versionInfo.isRevised ? "bg-green-50 border-green-200" : "bg-blue-50 border-blue-200"
          }`}
        >
          <div className="flex items-center gap-2">
            <span className={versionInfo.isRevised ? "text-green-600" : "text-blue-600"}>
              {versionInfo.isRevised ? "✨" : "📋"}
            </span>
            <span className={`font-medium ${versionInfo.isRevised ? "text-green-800" : "text-blue-800"}`}>
              {versionInfo.isRevised
                ? "This is your revised case study based on your feedback"
                : "This is the original version before revisions"}
            </span>
          </div>
        </div>
      )}

      {/* Metadata */}
      {caseStudy.metadata && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-medium text-gray-700 mb-2">Generation Details</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Source File:</span>
              <span className="ml-2 font-medium">{caseStudy.metadata.fileName}</span>
            </div>
            <div>
              <span className="text-gray-500">Word Count:</span>
              <span className="ml-2 font-medium">{caseStudy.metadata.wordCount}</span>
            </div>
            <div>
              <span className="text-gray-500">Job ID:</span>
              <span className="ml-2 font-medium font-mono text-xs">{caseStudy.metadata.job_id}</span>
            </div>
            <div>
              <span className="text-gray-500">File Key:</span>
              <span className="ml-2 font-medium font-mono text-xs" title={caseStudy.metadata.fileKey}>
                {caseStudy.metadata.fileKey ? `...${caseStudy.metadata.fileKey.slice(-8)}` : "N/A"}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Sticky Apply Changes Banner */}
      {isApplyBannerSticky && (
        <div className="fixed top-0 left-0 right-0 z-40 bg-blue-500 text-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex justify-between items-center">
              <div>
                <span className="font-medium text-sm">
                  {
                    Object.keys(customContent).filter(
                      (key) => customContent[key] && customContent[key].toString().trim(),
                    ).length
                  }{" "}
                  Custom Edits • {Object.keys(sectionFeedback).length} AI Revision Sections •{" "}
                  {Object.keys(selectedSectionsForFeedback).length} Mass Edit Sections
                </span>
              </div>
              <button
                onClick={() => setShowSubmitConfirm(true)}
                disabled={submitting}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  submitting
                    ? "bg-blue-400 text-blue-200 cursor-not-allowed"
                    : "bg-white text-blue-600 hover:bg-blue-50"
                }`}
              >
                {submitting ? "Processing..." : "Apply Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sticky Save New Case Study Banner */}
      {isMergeBannerSticky && viewMode === "merge" && (
        <div className="fixed top-0 left-0 right-0 z-40 bg-indigo-500 text-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex justify-between items-center">
              <div>
                <span className="font-medium text-sm">
                  🔀 Compare & Merge Versions
                </span>
              </div>
              <button
                onClick={handleMergeSubmit}
                disabled={submitting}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  submitting
                    ? "bg-indigo-400 text-indigo-200 cursor-not-allowed"
                    : "bg-white text-indigo-600 hover:bg-indigo-50"
                }`}
              >
                {submitting ? "Saving..." : "Save New Case Study"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showStartOverConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-3">Confirm Start Over</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to start over? This will clear your current case study and any unsaved changes.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowStartOverConfirm(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onStartOver()
                  setShowStartOverConfirm(false)
                }}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
              >
                Start Over
              </button>
            </div>
          </div>
        </div>
      )}

      {showSubmitConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-3">Confirm Changes</h3>
            <p className="text-gray-600 mb-6">
              Are you ready to apply your changes? Custom edits will be applied first, then revision feedback will be
              processed.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowSubmitConfirm(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleSubmitChanges()
                  setShowSubmitConfirm(false)
                }}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
              >
                Apply Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {showUnsavedChangesConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-3">Unsaved Changes</h3>
            <p className="text-gray-600 mb-6">
              You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelNavigation}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmNavigation}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
              >
                Leave Without Saving
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HTML Preview Modal */}
      {showHtmlPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <style>{`
            .slider::-webkit-slider-thumb {
              appearance: none;
              height: 16px;
              width: 16px;
              border-radius: 50%;
              background: #3b82f6;
              cursor: pointer;
              box-shadow: 0 2px 4px rgba(0,0,0,0.2);
            }
            .slider::-moz-range-thumb {
              height: 16px;
              width: 16px;
              border-radius: 50%;
              background: #3b82f6;
              cursor: pointer;
              border: none;
              box-shadow: 0 2px 4px rgba(0,0,0,0.2);
            }
            .slider::-webkit-slider-track {
              height: 6px;
              background: #e5e7eb;
              border-radius: 3px;
            }
            .slider::-moz-range-track {
              height: 6px;
              background: #e5e7eb;
              border-radius: 3px;
              border: none;
            }
            .slider:focus {
              outline: none;
              ring: 2px solid #3b82f6;
            }
          `}</style>
          <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full h-[90vh] flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-6">
                  <h3 className="text-lg font-bold text-gray-800">HTML Preview</h3>
                  
                  {/* Template Selection Tabs */}
                  <div className="flex bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => handleTemplateSwitch(TEMPLATE_NAMES.PAGE_1)}
                      className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                        selectedTemplate === TEMPLATE_NAMES.PAGE_1
                          ? "bg-white text-gray-900 shadow-sm"
                          : "text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      Page 1
                    </button>
                    <button
                      onClick={() => handleTemplateSwitch(TEMPLATE_NAMES.PAGE_2)}
                      className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                        selectedTemplate === TEMPLATE_NAMES.PAGE_2
                          ? "bg-white text-gray-900 shadow-sm"
                          : "text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      Page 2
                    </button>
                  </div>
                  
                  {/* PDF Export Button */}
                  <button
                    onClick={generatePDF}
                    disabled={pdfGenerating || htmlPreviewLoading || htmlPreviewError}
                    className="ml-4 px-3 py-1.5 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                  >
                    {pdfGenerating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Generating...
                      </>
                    ) : (
                      <>
                        <span>🖨️</span>
                        Print to PDF
                      </>
                    )}
                  </button>
                </div>
                
                <button
                  onClick={() => setShowHtmlPreview(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Text Sizing Controls */}
              <div className="space-y-2">
                
                {/* Template-Specific Controls */}
                {selectedTemplate === TEMPLATE_NAMES.PAGE_1 && (
                  <div className="flex flex-wrap gap-3 items-start">
                    {renderSizingControl('title', 'Title')}
                    {renderSizingControl('subtitle', 'Subtitle')}
                    {renderSizingControl('at_glance', 'At a Glance')}
                    {renderSizingControl('key_metrics_title', 'Key Metrics Title')}
                    {renderSizingControl('key_metrics_description', 'Key Metrics Desc')}
                    {renderSizingControl('challenges', 'Challenges')}
                    {renderSizingControl('objectives', 'Objectives')}
                    {renderSizingControl('outcomes', 'Outcomes')}
                    {renderSizingControl('testimonial', 'Testimonial')}
                  </div>
                )}
                
                {selectedTemplate === TEMPLATE_NAMES.PAGE_2 && (
                  <div className="flex flex-wrap gap-3 items-start">
                    {renderSizingControl('partnership_client_overview', 'Client Overview')}
                    {renderSizingControl('partnership_bullets', 'Partnership Bullets')}
                    {renderSizingControl('why_choose_riscpoint', 'Why Choose RISCPoint')}
                    {renderSizingControl('call_to_action', 'Contact CTA')}
                  </div>
                )}
              </div>
            </div>
            <div className="flex-1 p-4">
              {htmlPreviewLoading && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Rendering HTML preview...</p>
                  </div>
                </div>
              )}
              {htmlPreviewError && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <span className="text-red-500 text-4xl mb-4 block">⚠️</span>
                    <p className="text-red-600 mb-4">{htmlPreviewError}</p>
                    <button
                      onClick={handleHtmlPreview}
                      className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              )}
              {renderedHtml && !htmlPreviewLoading && !htmlPreviewError && (
                <iframe
                  ref={previewIframeRef}
                  srcDoc={renderedHtml}
                  className="w-full h-full border border-gray-200 rounded-md"
                  title="HTML Preview"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    </>
    </Tooltip.Provider>
  )
}

export default CaseStudyViewer
